import {
  Box,
  CardMedia,
  FormControl,
  FormHelperText,
  InputLabel,
  TextField,
  Theme,
} from '@mui/material';
import { LocalizationProvider, DesktopDatePicker } from '@mui/lab';
import PickersDay from '@mui/lab/PickersDay';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { SxProps } from '@mui/system';
import { useField, useFormikContext } from 'formik';
import { useState } from 'react';
import momentTimezone from 'moment-timezone';
import AdapterMoment from '@mui/lab/AdapterMoment';
import {
  enhanceCalendarWeekdayHeaders,
  getDateInputAriaLabel,
  getPickersDayAriaLabel,
  getSelectedDateAnnouncement,
  getWeekdayColumnIndex,
  useCalendarWeekdayHeaders,
} from './calendarWeekday';

const defaultTextFieldStyles: SxProps<Theme> = {
  'label + &': {
    mt: 4,
  },
  '& .MuiInputBase-root': {
    border: '1px solid #5E9BC6',
  },
  '& .MuiOutlinedInput-root': {
    position: 'relative',
    backgroundColor: 'common.white',
    border: '1px solid #5E9BC6',
    fontSize: 16,
    '&:hover': {
      borderColor: 'primary.main',
      '.MuiOutlinedInput-notchedOutline': {
        border: 0,
      },
    },
    '.MuiOutlinedInput-notchedOutline': {
      border: 0,
    },
    '.MuiInputBase-input': {
      p: '10px 12px',
    },
    '&:focus-visible': {
      outline: 'none',
    },
  },
};

const srOnlyLiveRegionSx: SxProps<Theme> = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export const TextDatePicker = ({
  label = '',
  placeholder = '',
  name,
  inputLabelStyles,
  textFieldStyles = defaultTextFieldStyles,
  maxDate = new Date(),
  minDate,
  disabled = false,
  required = false,
  onClick,
}: {
  label?: string;
  placeholder: string;
  value?: string | null;
  onClick?: () => void;
  name: string;
  inputLabelStyles?: SxProps<Theme>;
  textFieldStyles?: SxProps<Theme>;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
  required?: boolean;
}): JSX.Element => {
  const [field, meta, { setValue }] = useField(name);
  const [error, setError] = useState(false);
  const [monthStatus, setMonthStatus] = useState('');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { setFieldValue } = useFormikContext();
  useCalendarWeekdayHeaders(name, isCalendarOpen);
  const announceMonth = (date: Date | string | null) => {
    setMonthStatus(moment(date).format('MMMM YYYY'));
  };
  const announceSelection = (date: Date | string | null) => {
    const announcement = getSelectedDateAnnouncement(date);
    if (announcement) {
      setMonthStatus(announcement);
    }
  };
  const onChange = (date: any) => {
    date == 'Invalid Date' && setError(true);
    date != 'Invalid Date' && setError(false);
    const date1 = moment(new Date());
    const date2 =
      moment(date).get('hours') === 0 &&
      moment(date).get('minutes') === 0 &&
      moment(date).get('seconds') === 0
        ? moment(date)
            .add(date1.get('hours'), 'hours')
            .add(date1.get('minutes'), 'minutes')
            .add(date1.get('seconds'), 'seconds')
            .format()
        : moment(date).format();
    // console.log(date2);
    setValue(date2);
    announceSelection(date2);
    onClick && onClick();
  };
  const errorText = error ? 'Invalid Date' : meta.error && meta.touched ? meta.error : '';
  const timeZoneFromServer = 'Asia/Kolkata';
  const { moment } = new AdapterMoment({ instance: momentTimezone });
  moment.tz.setDefault(timeZoneFromServer);
  momentTimezone.tz.setDefault(timeZoneFromServer);
  // const dateWithTimeZone = moment(field.value).format();
  // useEffect(() => {
  //   setFieldValue(name, moment(field.value).format());
  // }, []);
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <FormControl variant="outlined" fullWidth sx={{ my: { xs: 1, md: 2 } }}>
        <Box id={`${name}-month-status`} role="status" aria-live="polite" sx={srOnlyLiveRegionSx}>
          {monthStatus}
        </Box>
        <InputLabel shrink htmlFor="date-picker" sx={{ ...inputLabelStyles }}>
          {label}
        </InputLabel>
        <DesktopDatePicker
          shouldDisableDate={(date) => {
            if (maxDate && moment(maxDate).isBefore(moment(date))) {
              return true;
            } else if (minDate && moment(minDate).isAfter(moment(date))) {
              return true;
            }
            return false;
          }}
          shouldDisableYear={(date) => {
            if (
              maxDate &&
              moment(new Date(maxDate).setFullYear(new Date(maxDate).getFullYear() + 1)).isBefore(
                moment(date)
              )
            ) {
              return true;
            } else if (minDate && moment(minDate).isAfter(moment(date))) {
              return true;
            }
            return false;
          }}
          components={{
            OpenPickerIcon: () => (
              <CardMedia component="img" src="/images/calender.svg" alt="Select Date" />
            ),
          }}
          componentsProps={{}}
          value={field.value}
          inputFormat="DD/MM/yyyy"
          onChange={onChange}
          disabled={disabled}
          onOpen={() => {
            setIsCalendarOpen(true);
            announceMonth(field.value || new Date());
            enhanceCalendarWeekdayHeaders(name);
          }}
          onClose={() => setIsCalendarOpen(false)}
          onMonthChange={(date) => {
            announceMonth(date);
            enhanceCalendarWeekdayHeaders(name);
          }}
          views={['year', 'month', 'day']}
          InputAdornmentProps={{ position: 'start' }}
          renderDay={(day, _selectedDates, pickersDayProps) => {
            const outsideCurrentMonth = Boolean(pickersDayProps.outsideCurrentMonth);
            const showDaysOutsideCurrentMonth = Boolean(
              pickersDayProps.showDaysOutsideCurrentMonth
            );
            const isHiddenFiller = outsideCurrentMonth && !showDaysOutsideCurrentMonth;
            const isDisabled = Boolean(pickersDayProps.disabled);
            const isSelected = Boolean(pickersDayProps.selected);
            const isToday = moment(day).isSame(moment(), 'day');

            if (isHiddenFiller) {
              return (
                <PickersDay
                  {...pickersDayProps}
                  aria-hidden
                  tabIndex={-1}
                  aria-label={undefined}
                />
              );
            }

            const weekdayHeaderId = `${name}-weekday-${getWeekdayColumnIndex(day)}`;
            return (
              <div
                role="cell"
                aria-describedby={isDisabled ? undefined : weekdayHeaderId}
                aria-hidden={isDisabled || undefined}>
                <PickersDay
                  {...pickersDayProps}
                  aria-selected={isSelected}
                  aria-label={
                    isDisabled
                      ? undefined
                      : getPickersDayAriaLabel(day, { isSelected, isToday })
                  }
                  tabIndex={isDisabled || outsideCurrentMonth ? -1 : pickersDayProps.tabIndex}
                />
              </div>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              id="date-picker"
              required={required}
              placeholder={placeholder}
              inputProps={{
                ...params.inputProps,
                autoComplete: 'bday',
                required,
                'aria-required': required,
                'aria-label': getDateInputAriaLabel(label, field.value),
              }}
              sx={{
                ...textFieldStyles,
                display: 'flex',
                flexDirection: 'row-reverse',
                '& .Mui-disabled': {
                  cursor: !disabled ? 'text' : 'not-allowed !important',
                  pointerEvents: 'all',
                },
              }}
              disabled={disabled}
            />
          )}
          disableCloseOnSelect={false}
          showToolbar={false}
        />
        {errorText && (
          <FormHelperText error sx={{ marginLeft: 'unset' }}>
            {errorText}
          </FormHelperText>
        )}
      </FormControl>
    </LocalizationProvider>
  );
};
